import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { CircleNotch, Paperclip, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOut } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;

// Feb 2026, user's explicit ask - inline, zoomable preview of a GRN's
// supplier invoice attachment, used in BOTH the pending-shipment
// approval flow (next to the AI Invoice Match panel) and the Confirmed
// GRN detail modal. Extracted into its own component so all call sites
// share one fetch/zoom implementation instead of duplicating it
// (GrnApprovalPage.jsx was already flagged as too large).
//
// Feb 2026 follow-up (user's real complaint: "the container of the
// invoice copy is very small") - approved option (shown via screenshot
// first, picked "option 4"): an "Expand" button opens the SAME
// preview large in a right-side drawer, while the GRN item table etc.
// behind it stays visible/scrollable (unlike a fullscreen modal). Kept
// self-contained here so every call site gets this for free with zero
// extra wiring.
//
// Feb 2026 retest fix (testing_agent iteration_295, HIGH): the Sheet
// was still fully MODAL by default (Radix locks body scroll + a
// bg-black/80 overlay intercepts every click), so the GRN form behind
// it was neither scrollable nor interactable - directly contradicted
// the approved "page stays visible/scrollable" intent of Option 4. Now
// uses `<Sheet modal={false}>` + `<SheetContent overlay={false}>` (new
// opt-in props on the shared ui/sheet.jsx, default unchanged for every
// other Sheet usage like NavTabs' mobile menu) and swallows outside-
// click/scroll so clicking the form never closes the drawer.
//
// Also fixed (same report, minor): zoom used CSS transform:scale,
// which doesn't grow the scrollable area - zoomed-in edges were
// clipped and unreachable. Switched to width-based sizing (`shrink-0`
// so flex can't shrink it back down) so overflow-auto can actually
// scroll to every edge; content is now top-aligned instead of
// vertically centered with large blank gaps.
export const InvoicePreviewPanel = ({ docCode, attachment, title = "Supplier Invoice Copy" }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const previewable = !!attachment?.filename && /\.(pdf|jpe?g|png)$/i.test(attachment.filename);
  const isPdf = (attachment?.filename || "").toLowerCase().endsWith(".pdf");

  useEffect(() => {
    let url = null;
    setPreviewError(false);
    setZoom(1);
    if (!attachment?.path || !docCode) {
      setPreviewUrl(null);
      return;
    }
    (async () => {
      try {
        const resp = await axios.get(`${API}/admin/grn/${docCode}/invoice-attachment`, { responseType: "blob" });
        url = window.URL.createObjectURL(resp.data);
        setPreviewUrl(url);
      } catch {
        setPreviewUrl(null);
        setPreviewError(true);
      }
    })();
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [attachment?.path, docCode]);

  if (!attachment?.path) return null;

  const zoomControls = (idPrefix) => previewable && previewUrl && !previewError && (
    <div className="flex items-center gap-1">
      <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-sm" disabled={zoom <= ZOOM_MIN} onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))} title="Zoom out" data-testid={`${idPrefix}-zoom-out`}>
        <MagnifyingGlassMinus size={13} />
      </Button>
      <button type="button" className="text-[11px] font-data text-[#667085] w-10 text-center hover:text-[#1D2939]" onClick={() => setZoom(1)} title="Reset zoom" data-testid={`${idPrefix}-zoom-reset`}>{Math.round(zoom * 100)}%</button>
      <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-sm" disabled={zoom >= ZOOM_MAX} onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))} title="Zoom in" data-testid={`${idPrefix}-zoom-in`}>
        <MagnifyingGlassPlus size={13} />
      </Button>
    </div>
  );

  const body = (heightClass, testIdSuffix = "") => (
    <div className={`flex-1 ${heightClass} overflow-auto flex items-start justify-center p-2`}>
      {!previewable ? (
        <span className="text-xs text-[#667085] text-center px-4" data-testid="grn-invoice-preview-unsupported">Preview not available for this file type ({attachment.filename}).</span>
      ) : previewError ? (
        <span className="text-xs text-[#B42318]" data-testid="grn-invoice-preview-error">Could not load the invoice preview</span>
      ) : !previewUrl ? (
        <CircleNotch size={20} className="animate-spin text-[#98A2B3]" />
      ) : isPdf ? (
        <iframe src={previewUrl} title="Supplier invoice preview" className="h-full min-h-[440px] border-0 shrink-0" style={{ width: `${zoom * 100}%` }} data-testid={`grn-invoice-preview-pdf${testIdSuffix}`} />
      ) : (
        <img src={previewUrl} alt="Supplier invoice" className="h-auto rounded-sm shrink-0" style={{ width: `${zoom * 100}%` }} data-testid={`grn-invoice-preview-image${testIdSuffix}`} />
      )}
    </div>
  );

  return (
    <>
      <div className="border border-[#D0D5DD] rounded-sm bg-[#F2F4F7] overflow-hidden flex flex-col" data-testid="grn-invoice-preview-panel">
        <div className="px-3 py-1.5 bg-white border-b border-[#D0D5DD] text-xs font-semibold text-[#475467] flex items-center justify-between gap-1.5">
          <span className="flex items-center gap-1.5"><Paperclip size={12} /> {title}</span>
          <div className="flex items-center gap-1.5">
            {zoomControls("grn-invoice-preview")}
            <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-sm" onClick={() => setDrawerOpen(true)} title="Open larger view" data-testid="grn-invoice-preview-expand">
              <ArrowsOut size={13} />
            </Button>
          </div>
        </div>
        {body("min-h-[260px] max-h-[480px]")}
      </div>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen} modal={false}>
        <SheetContent
          side="right"
          overlay={false}
          closeTestId="grn-invoice-preview-drawer-close"
          className="w-full sm:max-w-2xl p-0 flex flex-col shadow-2xl border-l-2"
          data-testid="grn-invoice-preview-drawer"
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <div className="pl-4 sm:pl-5 pr-14 py-3 border-b border-[#D0D5DD] flex items-center justify-between gap-2 shrink-0">
            <SheetTitle className="text-sm font-semibold text-[#1D2939] flex items-center gap-1.5"><Paperclip size={14} /> {title}</SheetTitle>
            {zoomControls("grn-invoice-drawer")}
          </div>
          <div className="flex-1 min-h-0 p-3">
            {body("h-full min-h-[300px]", "-drawer")}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
